import { uploadDiagnosticsRegistry } from "./diagnostics/UploadDiagnosticsRegistry";
import { attachChunkedUploadBridge } from "./bridge/ChunkedUploadBridge";
import { attachLegacyUploadBridge } from "./bridge/LegacyUploadBridge";
import { getAnalytics, bootstrapAnalytics } from "./analytics";
import { startNetworkTelemetry, getNetworkSnapshot } from "./network/NetworkTelemetry";
import { UPLOAD_EVENT } from "./diagnostics/UploadEvents";
import { captureDiagnosticSnapshot } from "./diagnostics/DiagnosticSnapshot";
import { getUploadSessionId } from "@/utils/uploadSessionId";

/**
 * The tiny public surface the upload services import.
 *
 * By exposing exactly `announceUpload()` and `announceUploadTerminal()` we
 * keep the invasive footprint inside ChunkedUploadService and
 * LegacyUploadService down to two imports and a handful of call sites. The
 * observability system doesn't need any more knobs than "here's an upload
 * that just started" and "here's how it ended" — every other event flows in
 * via the subscription-based bridges.
 */

/** @type {Map<string, () => void>} bridge disposers */
const bridgeDisposers = new Map();

/**
 * Called by ChunkedUploadService / LegacyUploadService the moment the
 * uploadId is minted. Registers the upload in the diagnostics registry, wires
 * the appropriate bridge, and emits UPLOAD_CREATED.
 *
 * @param {string} uploadId
 * @param {object} [context]
 * @param {"chunked" | "legacy"} [context.uploader]  defaults to "chunked"
 * @param {any}    [context.chunkedUploadService]     required for chunked bridge
 * @param {number} [context.fileCount]
 * @param {number} [context.totalSize]
 * @param {Array<string>} [context.mimeTypes]
 * @param {string} [context.packageName]
 * @param {string} [context.uploadSessionId]         if omitted, we resolve it
 * @returns {import('./diagnostics/UploadDiagnostics').UploadDiagnostics}
 */
export const announceUpload = (uploadId, context = {}) => {
  if (!uploadId) throw new Error("announceUpload requires uploadId");

  // Cheap idempotency: repeat calls just return the existing diagnostics.
  const existing = uploadDiagnosticsRegistry.get(uploadId);
  if (existing) {
    existing.setContext(context);
    return existing;
  }

  // Ensure the global network listener is running before we attach any bridge.
  startNetworkTelemetry();

  const sessionId = context.uploadSessionId || safeGetSessionId();
  const initialContext = {
    fileCount: context.fileCount,
    totalSize: context.totalSize,
    mimeTypes: context.mimeTypes,
    packageName: context.packageName,
    uploader: context.uploader || "chunked",
    uploadSessionId: sessionId,
  };
  const diag = uploadDiagnosticsRegistry.getOrCreate(uploadId, initialContext);
  diag.patchState({
    totalBytes: context.totalSize ?? null,
    chunkCount: context.fileCount ?? null,
    network: getNetworkSnapshot(),
  });

  // Emit UPLOAD_CREATED once so PostHog sees the ignition point even if the
  // package never receives a byte (e.g. rejected on init).
  diag.record(UPLOAD_EVENT.UPLOAD_CREATED, {
    fileCount: context.fileCount,
    totalSize: context.totalSize,
    mimeTypes: context.mimeTypes,
    packageName: context.packageName,
    uploader: initialContext.uploader,
    sessionId,
    // Capture PostHog session ids as part of the event so backend can store
    // them without a follow-up round-trip. get_session_id() is cheap (reads a
    // local cookie/localStorage entry) — safe to call synchronously.
    posthogSessionId: safeGetAnalyticsSessionId(),
    posthogSessionUrl: safeGetAnalyticsSessionUrl(),
    network: getNetworkSnapshot(),
  });

  // Also update PostHog Person Properties with the current active upload — this
  // makes cohort/funnel building trivial ("users with an in-flight upload").
  getAnalytics().setPersonProperties({ activeUploadId: uploadId });

  // Wire the correct bridge and remember the disposer.
  let disposer;
  if (initialContext.uploader === "legacy") {
    disposer = attachLegacyUploadBridge(diag);
  } else {
    disposer = attachChunkedUploadBridge(diag, context.chunkedUploadService);
  }
  bridgeDisposers.set(uploadId, disposer);

  // Fire the started snapshot IMMEDIATELY (no wait). Two reasons:
  //   1. Even if the user cancels or navigates away within the first second,
  //      the backend has a row keyed to this uploadId — otherwise it stays
  //      forever null and admins can't correlate anything.
  //   2. The PostHog session id column on the backend is sticky-non-null-wins
  //      (UploadDiagnosticSnapshotService.save), so a null id here does no
  //      damage — the backfill below will populate it once PostHog is ready.
  captureStartedSnapshotWithBackfill(uploadId, diag);

  return diag;
};

/**
 * Fire an immediate "started" diagnostic snapshot; if PostHog wasn't loaded
 * yet (getSessionId returned null), enqueue a second capture the moment the
 * analytics bootstrap resolves so the backend row picks up the session id.
 *
 * Backend semantics (UploadDiagnosticSnapshotService.save): the snapshot JSON
 * is latest-wins, but posthogSessionId / posthogSessionUrl only overwrite
 * when the incoming value is non-empty — so double-firing is safe and only
 * ever adds information.
 */
const captureStartedSnapshotWithBackfill = (uploadId, diag) => {
  captureDiagnosticSnapshot(diag, {
    trigger: "started",
    reason: "initial_visibility",
  })
    .then((snapshot) => {
      if (snapshot?.correlation?.posthogSessionId) return;
      return bootstrapAnalytics().then(() => {
        if (!uploadDiagnosticsRegistry.has(uploadId)) return;
        captureDiagnosticSnapshot(diag, {
          trigger: "started",
          reason: "posthog_ready_backfill",
        }).catch(() => {});
      });
    })
    .catch(() => {});
};

/**
 * Called when an upload reaches a terminal state that the subscription-based
 * bridges cannot infer on their own. In practice this means "cancelled by
 * user" (uploadTelemetryService reports FAILED for cancel + fail alike; the
 * bridge would emit UPLOAD_FAILED for both if we didn't step in here).
 *
 * @param {string} uploadId
 * @param {"cancelled" | "failed" | "completed"} reason
 * @param {object} [details]
 */
export const announceUploadTerminal = (uploadId, reason, details = {}) => {
  const diag = uploadDiagnosticsRegistry.get(uploadId);
  if (!diag) return;
  const event =
    reason === "cancelled"
      ? UPLOAD_EVENT.UPLOAD_CANCELLED
      : reason === "completed"
      ? UPLOAD_EVENT.UPLOAD_COMPLETED
      : UPLOAD_EVENT.UPLOAD_FAILED;
  diag.record(event, details);
  // Cancel doesn't flow through a HEALTH transition (StuckDetector fires
  // failure/completed snapshots on health change; cancel has no HEALTH state),
  // so without an explicit capture here the admin drawer would show nothing
  // for cancelled uploads. Fire-and-forget — captureDiagnosticSnapshot
  // swallows its own errors.
  if (reason === "cancelled") {
    captureDiagnosticSnapshot(diag, {
      trigger: "cancelled",
      reason: details.reason || "user_cancelled",
    }).catch(() => {});
  }
  disposeBridge(uploadId);
};

/**
 * Explicit disposal called by callers that own upload lifecycle (e.g. when
 * clearing local state on ownership loss). Idempotent.
 */
export const disposeUploadObservability = (uploadId) => {
  disposeBridge(uploadId);
};

const disposeBridge = (uploadId) => {
  const disposer = bridgeDisposers.get(uploadId);
  if (disposer) {
    try {
      disposer();
    } catch (_error) {
      /* ignore */
    }
    bridgeDisposers.delete(uploadId);
  }
};

const safeGetSessionId = () => {
  try {
    return getUploadSessionId();
  } catch (_error) {
    return null;
  }
};

const safeGetAnalyticsSessionId = () => {
  try {
    return getAnalytics().getSessionId();
  } catch (_error) {
    return null;
  }
};

const safeGetAnalyticsSessionUrl = () => {
  try {
    return getAnalytics().getSessionReplayUrl();
  } catch (_error) {
    return null;
  }
};
